import { goto } from "$app/navigation";
import { deleteTwoFactorAuth, postVerifyAndActivateTwoFactorAuth } from "../api/EnzianApi";
import { DOCUMENT_ROUTE, LOGIN_ROUTE } from "../helper/Routes";
import { addNotification } from "../notification/Notificationstore";
import { _ } from "svelte-i18n";
import { get, writable } from 'svelte/store';
import LoadingStore from "../basic/loading/LoadingStore";
//Get the base url (this will be overwritten by a rollup plugin)
export const base_url = import.meta.env.VITE_BASE_URL_REPLACE;
//Helper for disable double load when rerouted from private to public route
export const onLayoutLoadRefreshCalled = writable(false);
let tokenRefreshPromis = null;
export async function makeRequestWithSilentLogin(url, object) {
    object = {
        ...{
            headers: new Headers({
                'Authorization': 'Bearer ' + get(login)?.accessToken,
                'Content-Type': 'application/json'
            })
        },
        ...object
    };
    const response = await fetch(url, object);
    if (!response.ok) {
        //Check if we have an 401 // Unauthorized
        if (response.status === 401) {
            //Check if we have a promise going on to fetch the token
            if (!tokenRefreshPromis) {
                //Refresh the login
                tokenRefreshPromis = login.refresh();
            }
            //Return the promise.
            return tokenRefreshPromis
                .then(token => {
                //Clear the promise
                tokenRefreshPromis = null;
                if (token != null) {
                    object.headers.set('Authorization', 'Bearer ' + token);
                    //Make call a second time
                    return fetch(url, object).then(response => {
                        if (!response.ok) {
                            throw response;
                        }
                        else {
                            return response.enzianJSON();
                        }
                    });
                }
                else {
                    //Move the user to the login page
                    goto("/" + LOGIN_ROUTE);
                }
            });
        }
        else if (response.status === 403) {
            //We have a forbidden page.
            goto("/noauth", { replaceState: true });
        }
        else if (response.status === 405) {
            //Show the user that this action is not allowed
            await response.text().then(text => {
                const isAdmin = get(login)?.admin || get(login)?.systemAdmin;
                const button = {
                    label: isAdmin ? get(_)("general.licence_button_admin") : get(_)("general.licence_button"),
                    link: isAdmin ? "/settings?tab=Logins+%26+Lizenzen" : import.meta.env.VITE_LICENCE_URL_USER,
                    localNavigation: isAdmin || false
                };
                addNotification({
                    type: "error",
                    message: get(_)("general.err_not_licence").replace("{missing_licence}", get(_)(`general.licence_type_${text.substring(0, 1)}`)),
                    timeout: 10000,
                    button: button
                });
            });
            //Throw an error wich has the ignore flag set, so the handle error wont show any information.
            throw { ignore: true };
        }
        else {
            throw await response.enzianJSON();
        }
    }
    //Return the payload
    return await response.enzianJSON();
}
async function refreshLogin() {
    const response = await fetch(base_url + "/login/refresh", {
        method: 'GET',
        headers: new Headers({
            'Content-Type': 'application/json'
        }),
        credentials: 'include',
    });
    if (!response.ok) {
        throw await response.json();
    }
    return await response.json();
}
/**
 * Get the right icon for a given licence.
 * @param module
 * @returns
 */
export function selectRightIcon(module) {
    if (module.id === 1) {
        return "pencil-ruler";
    }
    else if (module.id === 2) {
        return "calculator";
    }
    else if (module.id === 4 || module.id === 5) {
        return "mobile-screen";
    }
    else {
        return "drafting-compass";
    }
}
async function logout() {
    const loginFromStore = get(login);
    if (loginFromStore == null)
        return { message: "No Login in store" };
    return makeRequestWithSilentLogin(base_url + "/logout", {
        method: 'POST',
        headers: new Headers({
            'Authorization': 'Bearer ' + loginFromStore.accessToken,
            'Content-Type': 'application/json'
        })
    });
}
async function forgotPassword(request) {
    let response = await fetch(base_url + "/login/forgot-password", {
        method: 'POST',
        headers: new Headers({
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify(request)
    });
    if (!response.ok) {
        throw await response.json();
    }
    return await response.json();
}
async function forgotLoginNames(request) {
    let response = await fetch(base_url + "/login/forgot-logins", {
        method: 'POST',
        headers: new Headers({
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify(request)
    });
    if (!response.ok) {
        throw await response.json();
    }
    return await response.json();
}
async function resetPassword(request) {
    let response = await fetch(base_url + "/login/reset-password", {
        method: 'POST',
        headers: new Headers({
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify(request)
    });
    if (!response.ok) {
        throw await response.json();
    }
    return await response.json();
}
export async function postResetTwoFactorAuthWithRecoveryCode(request) {
    let response = await fetch(base_url + "/login/two-factor-recovery", {
        method: 'POST',
        headers: new Headers({
            'Content-Type': 'application/json'
        }),
        body: JSON.stringify(request)
    });
    if (!response.ok) {
        throw await response.json();
    }
    return await response.json();
}
/**
 * Parse the jwt token so that we know which modules are set for the given login.
 * @param token
 * @returns
 */
function parseJwt(token) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
}
function createLoginStore() {
    //Creat the inital state
    const store = writable(null);
    const { subscribe, update, set, } = store;
    return {
        subscribe,
        logIn: async function (request) {
            //Make a normal login
            const rawResponse = await fetch(base_url + "/login", {
                method: 'POST',
                headers: new Headers({
                    'Content-Type': 'application/json'
                }),
                credentials: 'include',
                body: JSON.stringify(request)
            });
            if (!rawResponse.ok) {
                let body = await rawResponse.json();
                //Return the status code
                return rawResponse.status;
            }
            //Convert it to json
            const response = await rawResponse.json();
            const jwtToken = parseJwt(response.accessToken);
            response.jwt = jwtToken;
            //Trigger the update mechanic
            set(response);
            //Get the current URL so that we can check if we must route the user to the none mobile page
            const currentURL = window.location.href;
            if ((!currentURL.includes("mobile") && !currentURL.includes("localho")) && jwtToken.onlyMobile) {
                goto("/no-desktop");
            }
            else {
                if (currentURL.includes("mobile") && !currentURL.includes("localho")) {
                    goto(`/${DOCUMENT_ROUTE}`);
                }
                else {
                    goto("/");
                }
            }
            //Everything is oke
            return 200;
        },
        logout: async function () {
            //Make a the logout call
            try {
                //Logout from the server
                await logout();
                //Clear the state
                set(null);
                //Clean the whole stores.
                window.location.reload();
            }
            catch (error) {
                handleStoreError("general.err_logout", error);
            }
        },
        refresh: async function () {
            return navigator.locks.request('refresh-token-lock', { mode: 'exclusive' }, async () => {
                try {
                    //Make a call to the refresh logic
                    const refreshResponse = await refreshLogin();
                    const jwtToken = parseJwt(refreshResponse.accessToken);
                    refreshResponse.jwt = jwtToken;
                    //Update the store
                    set(refreshResponse);
                    //Get the current URL so that we can check if we must route the user to the none mobile page
                    const currentURL = window.location.href;
                    if ((!currentURL.includes("mobile") && !currentURL.includes("localho")) && jwtToken.onlyMobile) {
                        goto("/no-desktop");
                    }
                    return refreshResponse.accessToken;
                }
                catch (error) {
                    //Clear the store value
                    set(null);
                    return null;
                }
            });
        },
        forgotPassword: async function (loginName) {
            try {
                LoadingStore.set(true);
                //Make a call to the forgot password api
                await forgotPassword({
                    loginName: loginName
                });
                LoadingStore.set(false);
                //The reset worked, so redirect to login
                goto("/login");
                //Show the user a message
                addNotification({
                    message: get(_)("forgot_password.mail_sent_success"),
                    type: "success"
                });
            }
            catch (error) {
                LoadingStore.set(false);
                handleStoreError("forgot_password.err_forgot_password", error);
            }
        },
        forgotLoginName: async function (contactEmail) {
            try {
                LoadingStore.set(true);
                //Make a call to the forgot login name api
                await forgotLoginNames({
                    contactEmail: contactEmail
                });
                LoadingStore.set(false);
                //The reset worked, so redirect to login
                goto("/login");
                //Show the user a message
                addNotification({
                    message: get(_)("forgot_login_names.mail_sent_success"),
                    type: "success"
                });
            }
            catch (error) {
                LoadingStore.set(false);
                handleStoreError("forgot_login_names.err_forgot_login_name", error);
            }
        },
        passwordReset: async function (token, newPassword) {
            try {
                //Make a call to the refresh logic
                await resetPassword({
                    passwordResetToken: token,
                    newPassword: newPassword
                });
                //The reset worked, so redirect to login
                goto("/login");
                //Show the user a message
                addNotification({
                    message: get(_)("reset_password.reset_success"),
                    type: "success"
                });
            }
            catch (error) {
                handleStoreError("reset_password.err_reset_password", error);
            }
        },
        verifyTwoFactorAuthCodeForSetup: async function (targetLoginId, request) {
            //Send the verification code to the backend.
            const response = await postVerifyAndActivateTwoFactorAuth(targetLoginId, request);
            //Update the store
            update(loginInformation => {
                if (loginInformation == null)
                    return null;
                loginInformation.isTwoFactorAuth = true;
                return loginInformation;
            });
            //Make sure the user can see the recovery code
            return response.backupCode;
        },
        removeToFactorAuth: async function () {
            const value = get(store);
            if (value == null)
                return;
            try {
                //Send the verification code to the backend.
                const updatedLogin = await deleteTwoFactorAuth(value.id);
                //Update the store
                update(loginInformation => {
                    if (loginInformation == null)
                        return null;
                    loginInformation.isTwoFactorAuth = updatedLogin.isTwoFactorAuth;
                    return loginInformation;
                });
                addNotification({
                    type: 'success',
                    message: get(_)('settings.admin.two_factor_auth.lbl_deactivated')
                });
            }
            catch (err) {
                handleStoreError("settings.admin.two_factor_auth.err_deactivate", err);
            }
        },
        recoverTwoFactorAuthWithBackupCode: async function (request) {
            //Send the request to the backen
            await postResetTwoFactorAuthWithRecoveryCode(request);
        }
    };
}
export const login = createLoginStore();
/**
 * Handles general errors. This will check if the backend has sent some specific error message. Otherwise it will use the key given to look up
 * the error to display.
 * @param stringKey
 * @param error
 */
export function handleStoreError(stringKey, error) {
    if (error.localizedMessage) {
        addNotification({
            message: error.localizedMessage,
            type: "error",
            error: error
        });
    }
    else if (error.ignore) {
        //Possibility to ignore the error. This is mainly used to
        return;
    }
    else {
        addNotification({
            message: get(_)(stringKey),
            type: "error",
            error: error
        });
    }
}
